import React, { Fragment, memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import { ReviewDateField } from 'src/components/document-review/review/ReviewDateField'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { FormikFileInput } from 'src/components/shared/form/formik/FormikFileInput'
import { completeDriverLicenseFormValidator } from './CompleteDriverLicenseForm.validator'
import type { CompleteDriverLicenseFormValues } from './CompleteDriverLicenseForm.interface'

const FORM_ID = 'partner-driver-complete-driver-license-form'

const INITIAL_VALUES: CompleteDriverLicenseFormValues = { file: null, expiresAt: '' }

interface Props {
  isOpen: boolean
  stepLabel: string
  isSubmitting: boolean
  onSubmit(values: CompleteDriverLicenseFormValues): void
  onClose(): void
}

const CompleteDriverLicenseModalComponent = ({
  isOpen,
  stepLabel,
  isSubmitting,
  onSubmit,
  onClose,
}: Props) => {
  const handleSubmit = useCallback(
    (values: CompleteDriverLicenseFormValues) => {
      onSubmit(values)
    },
    [onSubmit],
  )

  return (
    <Formik<CompleteDriverLicenseFormValues>
      initialValues={INITIAL_VALUES}
      validationSchema={completeDriverLicenseFormValidator}
      onSubmit={handleSubmit}
    >
      <CommonModal
        isOpen={isOpen}
        title={`Complete ${stepLabel}`}
        className={tw`max-w-[560px]`}
        onClose={onClose}
        buttons={
          <Fragment>
            <Button size="small" color="grey" disabled={isSubmitting} onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="small"
              color="dark"
              type="submit"
              form={FORM_ID}
              loading={isSubmitting}
              disabled={isSubmitting}
              data-testid="partner-driver-driver-license-submit-button"
            >
              Submit
            </Button>
          </Fragment>
        }
      >
        <Form id={FORM_ID} css={tw`flex flex-col gap-4`}>
          {/* The backend also requires an earlier completed driver license step with the same
              label; when there is none its 400 message reaches the error toast. */}
          <Typography variant="body2" css={tw`text-shades-500`}>
            Upload the driver license and enter its expiration date.
          </Typography>
          <FormikFileInput<CompleteDriverLicenseFormValues>
            name="file"
            label="Driver license *"
            inputDataTestId="partner-driver-driver-license-file-input"
          />
          <ReviewDateField<CompleteDriverLicenseFormValues>
            name="expiresAt"
            label="Expiration date *"
          />
        </Form>
      </CommonModal>
    </Formik>
  )
}

export const CompleteDriverLicenseModal = memo(CompleteDriverLicenseModalComponent)
